import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AdminQuoteDetail,
  BillingPlanLimits,
  QuoteDraft,
  QuoteRequestPage,
  QuoteSnapshot,
} from '@zeroth/billing-contract';
import { apiRequest, ApiError, errorMessage } from '../lib/api';
import { Button } from '../components/Button';
import { quoteDollars } from './quoteForm';
const base = '/api/admin/billing/quote-requests';
const money = (cents: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
const label = (s: string) =>
  s
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());
const date = (s: string) => new Date(s).toLocaleString();
const field = 'block w-full rounded-lg border border-border bg-background px-3 py-2 text-sm';
const card = 'rounded-xl border border-border bg-card p-5 space-y-4';
function LoadState({ error, retry }: { error: unknown; retry: () => unknown }) {
  if (!error) return <p role="status">Loading quotes…</p>;
  if (error instanceof ApiError && error.status === 403)
    return <p role="alert">This area is available to Zeroth staff only.</p>;
  return (
    <div role="alert">
      <p>{errorMessage(error)}</p>
      <Button onClick={() => retry()}>Retry</Button>
    </div>
  );
}
export function AdminQuotesPage() {
  const { getToken, userId } = useAuth();
  const [search, setSearch] = useState(''),
    [page, setPage] = useState(1);
  const q = useQuery({
    queryKey: ['admin-quotes', userId, search, page],
    queryFn: async () =>
      (
        await apiRequest<{ data: QuoteRequestPage }>(
          getToken,
          'GET',
          base + '?' + new URLSearchParams({ page: String(page), search })
        )
      ).data,
    retry: false,
  });
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Custom quotes</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Review requests, agree fixed terms, and follow each quote through activation.
        </p>
      </div>
      <label className="block max-w-lg text-sm">
        Find an account
        <input
          className={field + ' mt-1'}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder="Account name or owner email"
        />
      </label>
      {!q.data ? (
        <LoadState error={q.error} retry={q.refetch} />
      ) : (
        <>
          <div className="space-y-3">
            {q.data.items.map((r) => (
              <Link
                key={r.id}
                to={'/admin/quotes/' + r.id}
                className="block rounded-xl border border-border bg-card p-4 hover:border-primary"
              >
                <div className="flex flex-wrap justify-between gap-2">
                  <strong>{r.accountName}</strong>
                  <span className="text-sm">{label(r.status)}</span>
                </div>
                <p className="mt-1 break-words text-sm text-muted-foreground">
                  {r.ownerEmails.join(', ')}
                </p>
                <p className="mt-2 text-sm">
                  {r.requestedCapacity || r.notes || 'Account usage review'}
                </p>
                <p className="mt-2 text-xs text-muted-foreground">
                  {date(r.createdAt)} ·{' '}
                  {r.source === 'CUSTOMER' ? 'Customer request' : 'Usage review'}
                  {r.quote
                    ? ' · ' +
                      money(r.quote.amountCents) +
                      ' / ' +
                      (r.quote.cadence === 'ANNUAL' ? 'year' : 'month')
                    : ''}
                </p>
              </Link>
            ))}
            {q.data.items.length === 0 && <p>No quote requests match this search.</p>}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="outline" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
              Previous
            </Button>
            <span className="text-sm">
              Page {page} · {q.data.total} requests
            </span>
            <Button
              variant="outline"
              disabled={page * q.data.pageSize >= q.data.total}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
export function AdminQuotePage() {
  const { id = '' } = useParams();
  const { getToken, userId } = useAuth();
  const client = useQueryClient();
  const [previewVersion, setPreviewVersion] = useState<string | null>(null);
  const key = ['admin-quote', userId, id];
  const q = useQuery({
    queryKey: key,
    queryFn: async () =>
      (await apiRequest<{ data: AdminQuoteDetail }>(getToken, 'GET', base + '/' + id)).data,
    retry: false,
  });
  const action = useMutation({
    mutationFn: async ({
      method = 'POST',
      suffix = '',
      body,
    }: {
      method?: 'POST' | 'PATCH';
      suffix?: string;
      body: unknown;
    }) =>
      (
        await apiRequest<{ data: AdminQuoteDetail }>(
          getToken,
          method,
          base + '/' + id + suffix,
          body
        )
      ).data,
    onSuccess: (data) => {
      client.setQueryData(key, data);
      void client.invalidateQueries({ queryKey: ['admin-quotes'] });
    },
  });
  return (
    <div className="space-y-5">
      <Link className="text-sm text-primary hover:underline" to="/admin/quotes">
        ← All quote requests
      </Link>
      {!q.data ? (
        <LoadState error={q.error} retry={q.refetch} />
      ) : (
        <>
          <div className="flex flex-wrap justify-between gap-3">
            <div>
              <h1 className="text-2xl font-semibold">{q.data.accountName}</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {q.data.currentSnapshot.owners.map((o) => o.email).join(', ')}
              </p>
            </div>
            <span>{label(q.data.status)}</span>
          </div>
          {action.error && (
            <p role="alert" className="rounded-lg border border-destructive p-3 text-sm">
              {errorMessage(action.error)}
            </p>
          )}
          <QuoteEditor
            key={id + ':' + q.data.draftRevision}
            detail={q.data}
            busy={action.isPending}
            preview={previewVersion === id + ':' + q.data.draftRevision}
            onPreview={(revision) =>
              setPreviewVersion(revision == null ? null : id + ':' + revision)
            }
            act={(args) => action.mutateAsync(args)}
          />
        </>
      )}
    </div>
  );
}
type Act = (args: {
  method?: 'POST' | 'PATCH';
  suffix?: string;
  body: unknown;
}) => Promise<AdminQuoteDetail>;
function QuoteEditor({
  detail: d,
  busy,
  act,
  preview,
  onPreview,
}: {
  detail: AdminQuoteDetail;
  busy: boolean;
  act: Act;
  preview: boolean;
  onPreview: (revision: number | null) => void;
}) {
  const [amount, setAmount] = useState(d.draft ? (d.draft.amountCents / 100).toFixed(2) : '');
  const [cadence, setCadence] = useState(d.draft?.cadence ?? d.cadence);
  const [validDays, setValidDays] = useState(d.draft?.validDays ?? 14);
  const [limits, setLimits] = useState<BillingPlanLimits>(
    structuredClone(d.draft?.limits ?? d.defaultLimits)
  );
  const [message, setMessage] = useState(d.draft?.message ?? '');
  const [notes, setNotes] = useState(d.internalNotes),
    [reason, setReason] = useState('');
  const [resend, setResend] = useState<string | null>(null);
  const cents = quoteDollars(amount);
  const locked = d.quotes.some((q) => q.acceptedAt) || d.status === 'CLOSED';
  const draft: QuoteDraft = {
    amountCents: cents ?? 0,
    cadence,
    validDays,
    limits,
    message,
  };
  const valid =
    cents !== null &&
    Number.isInteger(validDays) &&
    validDays >= 1 &&
    validDays <= 90 &&
    [
      limits.maxActiveSkus,
      limits.maxRevenueUsdCents,
      limits.users,
      limits.stores,
      limits.zenoSlots,
      limits.scanner.monthly,
      limits.scanner.live,
      limits.scanner.bulk,
      limits.solicitations?.monthly,
    ].every((n) => Number.isSafeInteger(n) && Number(n) > 0);
  const run = (args: Parameters<Act>[0]) => void act(args).catch(() => {});
  const save = async (showPreview: boolean) => {
    try {
      const saved = await act({
        method: 'PATCH',
        body: {
          expectedRevision: d.draftRevision,
          draft,
          internalNotes: notes,
        },
      });
      if (showPreview) onPreview(saved.draftRevision);
    } catch {
      /* Parent shows the error. */
    }
  };
  const showPreview = preview;
  const clearPreview = () => onPreview(null);
  const limitInput = (
    title: string,
    value: number | null | undefined,
    onChange: (n: number) => void
  ) => (
    <label className="block text-sm">
      {title}
      <input
        className={field + ' mt-1'}
        type="number"
        min="1"
        step="1"
        value={value ?? ''}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </label>
  );
  return (
    <>
      <div className="grid gap-5 lg:grid-cols-2">
        <section className={card}>
          <h2 className="font-semibold">Customer request</h2>
          <p className="whitespace-pre-wrap text-sm">
            {d.requestedCapacity || 'No additional capacity specified.'}
          </p>
          <p className="whitespace-pre-wrap text-sm">{d.notes}</p>
          <p className="text-sm">Preferred billing: {label(d.cadence)}</p>
          <Snapshot title="Current account usage" value={d.currentSnapshot} />
          <details>
            <summary className="cursor-pointer text-sm">Original request snapshot</summary>
            <Snapshot title="Usage when requested" value={d.snapshot} />
          </details>
        </section>
        <section className={card}>
          <h2 className="font-semibold">Review notes</h2>
          <p className="text-sm text-muted-foreground">Visible only to staff.</p>
          <textarea
            aria-label="Internal review notes"
            className={field}
            rows={4}
            maxLength={10000}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              disabled={busy || d.status === 'CLOSED'}
              onClick={() =>
                run({
                  method: 'PATCH',
                  body: {
                    expectedRevision: d.draftRevision,
                    internalNotes: notes,
                    startReview: true,
                  },
                })
              }
            >
              Start review / save notes
            </Button>
          </div>
          <label className="block text-sm">
            Reason for account action
            <input
              className={field + ' mt-1'}
              maxLength={1000}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              disabled={
                busy ||
                reason.trim().length < 3 ||
                d.trialExtensionUsed ||
                !d.currentSnapshot.trialEndsAt ||
                ['ACTIVE', 'CLOSED', 'DECLINED', 'EXPIRED'].includes(d.status)
              }
              onClick={() => run({ suffix: '/extend-trial', body: { reason } })}
            >
              {d.trialExtensionUsed ? 'Trial extension used' : 'Extend trial seven days'}
            </Button>
            <Button
              variant="outline"
              disabled={busy || reason.trim().length < 3 || d.status === 'CLOSED'}
              onClick={() => run({ suffix: '/close', body: { reason } })}
            >
              Close request
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Trial extensions are once per account. Billing must be resolved before closing an
            accepted request.
          </p>
        </section>
      </div>
      {!locked && (
        <section className={card}>
          <h2 className="font-semibold">{d.quotes.length ? 'Revised quote' : 'Quote draft'}</h2>
          {showPreview && d.draft ? (
            <div
              role="dialog"
              aria-label="Preview quote"
              className="space-y-4 rounded-xl border border-primary p-4"
            >
              <h3 className="font-semibold">Ready to send to the account owner</h3>
              <p>
                {money(d.draft.amountCents)} USD / {d.draft.cadence === 'ANNUAL' ? 'year' : 'month'}{' '}
                · valid {d.draft.validDays} days from sending
              </p>
              <Limits limits={d.draft.limits} />
              <p className="whitespace-pre-wrap">{d.draft.message}</p>
              <p className="text-sm text-muted-foreground">
                Sending publishes this quote in Billing and emails the current account owners.
              </p>
              <div className="flex gap-2">
                <Button
                  disabled={busy}
                  onClick={() => {
                    void act({
                      suffix: '/send',
                      body: { draftRevision: d.draftRevision },
                    })
                      .then(clearPreview)
                      .catch(() => {});
                  }}
                >
                  {d.quotes.length ? 'Send revised quote' : 'Send quote'}
                </Button>
                <Button variant="outline" disabled={busy} onClick={clearPreview}>
                  Back to draft
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-3">
                <label className="block text-sm">
                  Fixed price USD
                  <input
                    aria-label="Fixed price USD"
                    className={field + ' mt-1'}
                    inputMode="decimal"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="Enter agreed price"
                  />
                </label>
                <label className="block text-sm">
                  Billing frequency
                  <select
                    className={field + ' mt-1'}
                    value={cadence}
                    onChange={(e) => setCadence(e.target.value as QuoteDraft['cadence'])}
                  >
                    <option value="MONTHLY">Monthly</option>
                    <option value="ANNUAL">Annual</option>
                  </select>
                </label>
                {limitInput('Valid for days', validDays, setValidDays)}
              </div>
              <h3 className="text-sm font-semibold">Included limits</h3>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {limitInput('Saved SKUs', limits.maxActiveSkus, (n) =>
                  setLimits({ ...limits, maxActiveSkus: n })
                )}
                <label className="block text-sm">
                  Sales per 30 days (USD)
                  <input
                    className={field + ' mt-1'}
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={(limits.maxRevenueUsdCents ?? 0) / 100}
                    onChange={(e) =>
                      setLimits({
                        ...limits,
                        maxRevenueUsdCents:
                          quoteDollars(e.target.value, Number.MAX_SAFE_INTEGER) ?? 0,
                      })
                    }
                  />
                </label>
                {limitInput('Users (including invitations)', limits.users, (n) =>
                  setLimits({ ...limits, users: n })
                )}
                {limitInput('Connected stores', limits.stores, (n) =>
                  setLimits({ ...limits, stores: n })
                )}
                {limitInput('Zeno slots', limits.zenoSlots, (n) =>
                  setLimits({ ...limits, zenoSlots: n })
                )}
                {limitInput('Scanner enrichments per month', limits.scanner.monthly, (n) =>
                  setLimits({
                    ...limits,
                    scanner: { ...limits.scanner, monthly: n },
                  })
                )}
                {limitInput('Scanner per live session', limits.scanner.live, (n) =>
                  setLimits({
                    ...limits,
                    scanner: { ...limits.scanner, live: n },
                  })
                )}
                {limitInput('Scanner per bulk job', limits.scanner.bulk, (n) =>
                  setLimits({
                    ...limits,
                    scanner: { ...limits.scanner, bulk: n },
                  })
                )}
                {limitInput('Review requests per month', limits.solicitations?.monthly, (n) =>
                  setLimits({ ...limits, solicitations: { monthly: n } })
                )}
              </div>
              <label className="block text-sm">
                Message to the customer
                <textarea
                  className={field + ' mt-1'}
                  rows={3}
                  maxLength={4000}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </label>
              {!d.currentSnapshot.complete && (
                <p role="alert" className="text-sm">
                  Usage evidence is incomplete. You can save a draft; sending requires complete
                  usage.
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                The fixed price includes any agreed discounts. All limits must cover the account’s
                current usage.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  disabled={!valid || busy}
                  onClick={() => void save(false)}
                >
                  Save draft
                </Button>
                <Button
                  disabled={!valid || busy || !d.currentSnapshot.complete}
                  onClick={() => void save(true)}
                >
                  Preview and send
                </Button>
              </div>
            </>
          )}
        </section>
      )}
      {d.quotes.length > 0 && (
        <section className={card}>
          <h2 className="font-semibold">Quote history</h2>
          {d.quotes.map((q) => (
            <div key={q.id} className="border-t border-border pt-3 space-y-2 text-sm">
              <strong>
                Version {q.version} · {money(q.amountCents)} USD /{' '}
                {q.cadence === 'ANNUAL' ? 'year' : 'month'}
              </strong>
              <p>
                {q.acceptedAt
                  ? 'Accepted ' + date(q.acceptedAt)
                  : q.supersededAt
                    ? 'Superseded'
                    : q.declinedAt
                      ? 'Declined'
                      : 'Valid through ' + date(q.expiresAt)}
              </p>
              {q.limits && <Limits limits={q.limits} />}
              <p className="whitespace-pre-wrap">{q.message}</p>
            </div>
          ))}
          {d.nextAction && (
            <p>
              Next step: {label(d.nextAction)}
              {d.effectiveAt ? ' · ' + date(d.effectiveAt) : ''}
            </p>
          )}
        </section>
      )}
      <section className={card}>
        <h2 className="font-semibold">Email delivery</h2>
        {d.notifications.map((n) => (
          <div
            key={n.id}
            className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3 text-sm"
          >
            <div>
              <strong>{label(n.kind)}</strong>
              <p>
                {n.audience === 'STAFF' ? 'Admin inbox' : 'Account owner'} · {label(n.status)}
                {n.sentAt ? ' · ' + date(n.sentAt) : ''}
              </p>
              {n.lastError && <p>{n.lastError}</p>}
            </div>
            {['REVIEW', 'RETRY'].includes(n.status) && (
              <Button variant="outline" disabled={busy} onClick={() => setResend(n.id)}>
                Review resend
              </Button>
            )}
          </div>
        ))}
        {!d.notifications.length && (
          <p className="text-sm text-muted-foreground">No notifications queued.</p>
        )}
        {resend && (
          <div className="rounded-lg border p-3 space-y-3">
            <p className="text-sm">
              If delivery was uncertain, the recipient may already have this email. Send it again?
            </p>
            <div className="flex gap-2">
              <Button
                disabled={busy}
                onClick={() => {
                  void act({
                    suffix: '/notifications/' + resend + '/retry',
                    body: { confirmResend: true },
                  })
                    .then(() => setResend(null))
                    .catch(() => {});
                }}
              >
                Confirm resend
              </Button>
              <Button variant="outline" onClick={() => setResend(null)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </section>
      <section className={card}>
        <h2 className="font-semibold">Activity</h2>
        {d.audit.map((a) => (
          <details key={a.id} className="text-sm">
            <summary className="cursor-pointer">
              {label(a.kind)} · {a.actorId ?? 'System'} · {date(a.createdAt)}
            </summary>
            <p className="mt-2 whitespace-pre-wrap">{auditDescription(a.details, d)}</p>
          </details>
        ))}
      </section>
    </>
  );
}
function Snapshot({ title, value: s }: { title: string; value: QuoteSnapshot }) {
  return (
    <div className="space-y-2 text-sm">
      <h3 className="font-semibold">{title}</h3>
      <p className="text-xs text-muted-foreground">Captured {date(s.capturedAt)}</p>
      {!s.complete && <p role="alert">Incomplete: {s.reasons.join(', ')}</p>}
      <div className="grid grid-cols-2 gap-2">
        <Value name="Saved SKUs">{s.skuCount?.toLocaleString() ?? 'Unavailable'}</Value>
        <Value name="Sales / 30 days">
          {s.revenueUsdCents == null ? 'Unavailable' : money(s.revenueUsdCents)}
        </Value>
        <Value name="Users / pending invitations">
          {s.users} / {s.pendingInvitations}
        </Value>
        <Value name="Stores">{s.stores}</Value>
        <Value name="Zeno slots">{s.zenoSlots}</Value>
        <Value name="Trial ends">{s.trialEndsAt ? date(s.trialEndsAt) : 'No trial'}</Value>
      </div>
      {s.subscription && (
        <p>
          Current plan: {s.subscription.tier} · {money(s.subscription.amountCents)} /{' '}
          {label(s.subscription.cadence)} · {label(s.subscription.status)} · renewal{' '}
          {date(s.subscription.periodEnd)}
        </p>
      )}
      {s.usageWindows.map((w, i) => (
        <p key={i} className="text-xs">
          {w.name}: {w.startDate} through {w.endDate}
        </p>
      ))}
      <details>
        <summary className="cursor-pointer">Current allowances</summary>
        <Limits limits={s.planLimits} />
      </details>
    </div>
  );
}
function Value({ name, children }: { name: string; children: ReactNode }) {
  return (
    <div>
      <span className="block text-xs text-muted-foreground">{name}</span>
      {children}
    </div>
  );
}
function Limits({ limits: l }: { limits: BillingPlanLimits }) {
  return (
    <p className="text-sm">
      {l.maxActiveSkus?.toLocaleString() ?? 'Agreed'} SKUs ·{' '}
      {l.maxRevenueUsdCents == null
        ? 'Agreed sales'
        : money(l.maxRevenueUsdCents) + ' sales / 30 days'}{' '}
      · {l.users ?? 'Agreed'} users · {l.stores ?? 'Agreed'} stores · {l.zenoSlots ?? 'Agreed'} Zeno
      slots · {l.scanner.monthly.toLocaleString()} scanner enrichments / month ({l.scanner.live} /
      live session, {l.scanner.bulk} / bulk job) ·{' '}
      {l.solicitations?.monthly.toLocaleString() ?? 'Existing'} review requests / month
    </p>
  );
}

function auditDescription(payload: unknown, detail: AdminQuoteDetail) {
  const event = payload as Record<string, unknown>;
  const data = (event?.details ?? event) as Record<string, unknown>;
  if (!data) return '';
  const version = data.version ?? detail.quotes.find((q) => q.id === data.quoteId)?.version;
  return [
    version ? `Quote version ${version}` : null,
    typeof data.reason === 'string' ? data.reason : null,
    typeof data.endsAt === 'string' ? `Trial ends ${date(data.endsAt)}` : null,
    data.attempt ? `Delivery attempt ${data.attempt}` : null,
    data.draftChanged ? 'Draft updated' : null,
    data.notesChanged ? 'Internal notes updated' : null,
  ]
    .filter(Boolean)
    .join(' · ');
}
